import { Layer } from '@/core/models/layer';
import { CurrentUser, User } from '@/core/models/user';
import { LoadingService, PageLoadingState } from '@/core/services/loading.service';
import { TileService } from '@/core/services/open-api/tile.service';
import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { LayerService } from '@/core/services/open-api/layer.service';
import { ActivatedRoute, Router } from '@angular/router';
import { ConfirmationService, MenuItem } from 'primeng/api';
import { firstValueFrom, forkJoin, Observable, Subscription } from 'rxjs';
import { Tile } from '@/core/models/tile';
import { TileParent, TileVM } from '@/core/models/tile-vm';
import { TileVmMapper } from '@/core/models/tile-vm-mapper';
import { TileType } from '@/core/models/tile-type';
import { TranslateService } from '@ngx-translate/core';
import { TablePageEvent, TableRowSelectEvent } from 'primeng/table';
import { UserService } from '@/core/services/open-api/user.service';
import { UserPermission } from '@/core/models/user-permission';
import { RoleService } from '@/core/services/role.service';
import { Group } from '@/core/models/group.interface';
import { Popover } from 'primeng/popover';
import { BulkTileDeletionResult } from '@/core/models/bulk-tile-deletion-result';
import { ConfirmDialog } from 'primeng/confirmdialog';
import { HttpErrorResponse } from '@angular/common/http';
import { BreadcrumbService } from '@/theme/service/breadcrumb.service';
import { IntegrationService } from '@/core/services/open-api/integration.service';
import { AvailableIntegrations, IntegrationConfig } from '@/core/models/IntegrationEntity';
import {
    DropdownPanelOptions,
    getDropdownPanelOptions,
} from '@/common/library/dropdown-panel/dropdown-panel.component';
import { ToastService } from '@/core/services/toast.service';
import { InitializationManager } from '@/core/utils/initialization-manager';
import { CloneUtils } from '@/core/utils/clone-utils';
import { IntegrationUtils } from '@/core/utils/integration-utils';
import { TimeBudget } from '@/core/models/time-budget';
import { TimeBudgetService } from '@/core/services/open-api/time-budget.service';
import { TimeBudgetUtils } from '@/core/utils/time-budget-utils';
import { TimeBudgetView } from './time-budget-view';

@Component({
    selector: 'app-tiles',
    templateUrl: './tiles.component.html',
    styleUrls: ['./tiles.component.scss'],
    standalone: false,
})
export class TilesComponent implements OnInit, OnDestroy {
    @ViewChild('confirmPanel') confirmPanel: Popover | undefined;
    @ViewChild('menuPanel') menuPanel!: Popover;
    @ViewChild('deleteTileDialog') deleteTileDialog!: ConfirmDialog;
    tileParents: TileParent[] = [];
    filteredTiles: TileVM[] = [];
    selectedTiles = new Set<TileVM>();
    selectedTile: TileVM | null = null;
    tileToEdit?: TileVM;
    hasParent = false;
    isFirstLayer = false;
    isSecondLayer = false;
    isThirdLayer = false;
    areAllTilesSelected = false;
    isPro = false;
    parentLayerName = '';
    firstPagination = 0;
    paginationRows = 10;
    showAddEditDialog = false;
    layerName = '';
    currentUser?: CurrentUser;
    actionMenu: MenuItem[] = []; // Ticket #193
    loading: boolean = false;
    loadingState: number | null = null;
    TileType = TileType;
    tilesHaveActivities = false;
    tileStatusOptions = [
        { label: 'Active', value: false },
        { label: 'Archived', value: true },
    ];
    selectedTileStatus = false;
    areAllSelTilesFav = false;
    layers: Layer[] = [];
    isBulkDelete = false;
    isAdminOrManager = false;
    isAdmin = false;
    activeIntegrations: IntegrationConfig[] = [];
    showImportModalMap: Partial<Record<AvailableIntegrations, boolean>> = {};
    importOptions: DropdownPanelOptions = getDropdownPanelOptions();
    importServiceIdentifier: AvailableIntegrations = AvailableIntegrations.Sevdesk;
    public showArchived = false;
    layerId = 0;
    public allTiles: Tile[] | null = null;
    public allTilesOfLayer: Tile[] | null = null;
    private subscriptions = new Subscription();
    private initMgr: InitializationManager;
    isSingleUser: boolean = false;
    canReadTimeBudgets: boolean = false;
    timeBudgetViews = new Map<number, TimeBudgetView>();
    // switched on budgets by tile id
    private timeBudgets = new Map<number, TimeBudget>();
    // saved in the dialog after the list was requested, so it wins over that list until the next one is requested
    private savedTimeBudget?: TimeBudget;
    private timeBudgetSubscription?: Subscription;

    constructor(
        private readonly layerService: LayerService,
        private readonly tileService: TileService,
        private readonly route: ActivatedRoute,
        private readonly router: Router,
        private readonly loadingService: LoadingService,
        private readonly translate: TranslateService,
        public readonly userService: UserService,
        public readonly roleService: RoleService,
        private readonly toastService: ToastService,
        private readonly confirmationService: ConfirmationService,
        private readonly breadcrumbService: BreadcrumbService,
        private readonly integrationService: IntegrationService,
        private readonly timeBudgetService: TimeBudgetService,
    ) {
        this.initMgr = new InitializationManager(this.loadingService, this.constructor.name);
    }

    openImportModalForService(service: AvailableIntegrations): void {
        this.importServiceIdentifier = service;
        this.showImportModalMap[service] = true;
    }

    onImportSuccess(): void {
        // Refresh the tile list after a successful import
        void this.loadTiles(true);
        this.onTilesModified();
    }

    private populateImportOptions(): void {
        if (this.activeIntegrations.length > 0) {
            this.importOptions = getDropdownPanelOptions();
            for (const integration of this.activeIntegrations) {
                const needsReauth = integration.authStatus === 'invalid';
                const isRateLimited = integration.authStatus === 'rate-limited';
                const isDisabled = !this.isPro || needsReauth || isRateLimited;
                // Pro takes priority: if user is not Pro, always show the upgrade tooltip.
                // Only show the reauth tooltip when the user IS Pro but the token is invalid.

                // Note: tooltip is actually meant to be a localization key. In this case instead the already translated /
                //       localized value is assigned. This is actually not an issue because the translate pipe will return
                //       the value as is if it does not match any localization.
                //       Still this code should be optimized some time...
                //       ( => Major refactor of ExportActionsPanelComponent and DropdownPanelComponent)
                let tooltip = '';
                if (!this.isPro) {
                    // TODO check if this pro tooltip can be removed, if the buttons are even shown when not pro
                    tooltip = IntegrationUtils.transWithIntName(this.translate, integration.service, 'int_proFeature');
                } else if (needsReauth) {
                    tooltip = IntegrationUtils.transWithIntName(
                        this.translate,
                        integration.service,
                        'int_invoice_reauth_required_tooltip',
                    );
                } else if (isRateLimited) {
                    // Note: rate limiting is a temporary condition, not a broken connection - do not offer a
                    // reauth/settings link for it, just explain that it's temporary.
                    tooltip = IntegrationUtils.transWithIntName(
                        this.translate,
                        integration.service,
                        'int_invoice_rate_limited_tooltip',
                    );
                }

                const routerLink =
                    this.isPro && needsReauth && this.isAdmin
                        ? IntegrationUtils.getDetailsPageLink(integration.service)
                        : '';
                const routerLinkLabel =
                    this.isPro && needsReauth ? (this.isAdmin ? 'int_open_settings' : 'int_reauth_contact_admin') : '';
                this.importOptions.labels.push(IntegrationUtils.getI18nPrefix(integration.service) + '.name');
                this.importOptions.icons.push('pi pi-download');
                this.importOptions.functions.push(async () => {
                    this.openImportModalForService(integration.service);
                });
                this.importOptions.classes.push('p-button-text p-button-secondary');
                this.importOptions.disabled.push(isDisabled);
                this.importOptions.tooltips.push(tooltip);
                this.importOptions.tooltipLinks.push(routerLink);
                this.importOptions.tooltipLinkLabels.push(routerLinkLabel);
                this.importOptions.tooltipAutoHide.push(!routerLinkLabel);
            }
        } else {
            this.importOptions = getDropdownPanelOptions({
                labels: ['nav_integrations'],
                icons: ['pi pi-th-large'],
                functions: [
                    async (): Promise<void> => {
                        await this.router.navigate(['/settings/integrations']);
                    },
                ],
                classes: ['p-button-text p-button-secondary'],
            });
        }
    }

    ngOnInit(): void {
        this.setLoadingState('initializing');

        this.initMgr
            .addDependency(this.userService.currentUser$, {
                name: 'currentUser$',
                callback: (user) => {
                    this.currentUser = user;
                    this.isPro = user.isPro();
                    this.isAdminOrManager = user.isAdminOrManager();
                    this.isAdmin = user.isAdmin();
                },
            })
            .addDependency(this.userService.account$, {
                name: 'account$',
                callback: (account) => {
                    this.isSingleUser = account.getUsers().length === 1;
                },
            })
            .addDependency(this.layerService.layers$, {
                name: 'layers$',
                callback: (layers) => {
                    this.layers = layers;
                    this.onLayerChange();
                },
            })
            .addDependency(this.integrationService.integrations$, {
                name: 'integrations$',
                callback: (integrations) => {
                    this.activeIntegrations = integrations.filter((i) => i.status === 'active');
                    this.populateImportOptions();
                },
            })
            .onInit(async ([_user, _account, _layers, _integrations]) => {
                void this.loadTiles(); // note: loadTiles() performs updateLoadingState();
            })
            .onChange(async ([_user, _account, _layers, _integrations]) => {
                await this.loadTiles(); // note: loadTiles() performs updateLoadingState();
            })
            .initialize();

        this.route.params.subscribe((val) => {
            this.layerId = +val['id'];
            this.onLayerChange();
            this.updateLoadingState();
            this.loadTimeBudgets(); // time tracked elsewhere changes their usage, so show the current numbers per layer
        });

        this.subscriptions.add(
            this.timeBudgetService.canRead$.subscribe((canRead) => {
                this.canReadTimeBudgets = canRead;
                this.loadTimeBudgets();
            }),
        );
    }

    onLayerChange(): void {
        if (this.layerId === 0 || this.layers.length === 0) {
            return;
        }
        this.isFirstLayer = this.layers[0].id === this.layerId;
        this.isSecondLayer = this.layers[1].id === this.layerId;
        this.isThirdLayer = this.layers[2].id === this.layerId;
        this.layerName = this.layers.find((l) => l.id === this.layerId)?.name ?? '';
        this.breadcrumbService.addBreadcrumb(TilesComponent, { breadcrumb: this.layerName });
        this.onTilesOrLayerUpdate();
    }

    /**
     * Fetches the tiles from the backend and initializes the tiles
     * If manager or admin, fetch all tiles, else fetch assigned tiles ONLY
     */
    private async loadTiles(forceRefresh = false): Promise<void> {
        this.setLoadingState('data-loading');
        this.allTiles = await firstValueFrom(this.tileService.loadTiles(forceRefresh));
        this.onTilesOrLayerUpdate();
        this.setLoadingState('complete');
        this.updateLoadingState();
    }

    deleteTile(): void {
        if (!this.selectedTile && !this.isBulkDelete) {
            this.toastService.warnNoTranslate('No Selection', 'No tile selected for deletion.');
            return;
        }
        const ids = this.isBulkDelete
            ? [...this.selectedTiles].map((t) => t.id)
            : this.selectedTile
              ? [this.selectedTile.id]
              : [];

        if (this.isBulkDelete) {
            this.performBulkDeletion(ids);
        } else {
            this.performSingleDeletion(ids[0]);
        }
    }

    private performBulkDeletion(ids: number[]): void {
        this.tileService.bulkTileDeletion(ids).subscribe({
            next: (result: BulkTileDeletionResult) => {
                this.handleBulkDeletionSuccess(result);
            },
            error: (err: HttpErrorResponse) => {
                this.handleDeletionError(err);
            },
        });
    }

    private performSingleDeletion(id: number): void {
        this.tileService.deleteTileById(id).subscribe({
            next: () => {
                this.handleSingleDeletionSuccess();
            },
            error: (err: HttpErrorResponse) => {
                this.handleDeletionError(err);
            },
        });
    }

    private handleBulkDeletionSuccess(result: BulkTileDeletionResult): void {
        // Remove successfully deleted tiles from the filteredTiles and allTiles
        this.removeTilesFromLists(result.deletedTileIds);
        this.resetTileSelection();

        if (result.errorCount > 0) {
            this.toastService.warnNoTranslate(
                this.translate.instant('partial_bulk_tile_deletion_success_header'),
                this.translate.instant('partial_bulk_tile_deletion_success', {
                    successCount: result.successCount,
                    errorCount: result.errorCount,
                }),
            );
        } else {
            this.toastService.successNoTranslate(
                this.translate.instant('state_deleted'),
                this.translate.instant('bulk_tile_deletion_success', {
                    layerName: this.layerName,
                }),
            );
        }

        this.onTilesModified();
        this.finalizeDeletion();
    }

    private handleSingleDeletionSuccess(): void {
        // Remove deleted tile from the filteredTiles and allTiles
        if (this.selectedTile) {
            this.removeTilesFromLists([this.selectedTile.id]);
        }
        this.resetTileSelection();

        this.toastService.success('state_deleted', 'deletion_success');

        this.onTilesModified();
        this.finalizeDeletion();
    }

    private removeTilesFromLists(tileIds: number[]): void {
        this.filteredTiles = this.filteredTiles.filter((tile) => !tileIds.includes(tile.id));
        this.attachUserAccessColumn(this.filteredTiles);

        if (this.allTiles) {
            this.allTiles = this.allTiles.filter((tile) => !tileIds.includes(tile.id));
            this.onTilesOrLayerUpdate();
        }
    }

    private resetTileSelection(): void {
        this.selectedTile = null;
        this.tileToEdit = undefined;
        this.selectedTiles.clear();
    }

    private handleDeletionError(err: HttpErrorResponse): void {
        console.error(err);
        this.toastService.error('standard_error', 'tile_deletion_error_msg');
    }

    private finalizeDeletion(): void {
        this.deleteTileDialog.hide();
        this.isBulkDelete = false;
    }

    deleteBulkConfirm(): void {
        this.isBulkDelete = true;
        TilesComponent.showConfirmDeleteDialog(
            this.confirmationService,
            this.translate,
            this.selectedTiles.size + ' ' + this.layerName,
        );
    }

    private updatePage(refetchNeeded: boolean = false): void {
        if (refetchNeeded) {
            void this.loadTiles();
        }
    }

    public truncateText(text: string, maxLength: number): string {
        if (text.length > maxLength) {
            const truncatedText = text.slice(0, maxLength - 3);
            return truncatedText + '...';
        } else {
            return text;
        }
    }

    private updateTile(tile: Tile): void {
        if (this.allTiles === null) return;
        const index = this.allTiles.findIndex((t) => t.id === tile.id);
        if (index >= 0) {
            this.allTiles[index] = tile;
            this.onTilesOrLayerUpdate();
        }
    }

    private updateLoadingState(): void {
        if (this.allTiles === null) return;
        const tileCheck = this.allTiles.filter((t) => t.layer === this.layerId).length > 0;
        if (this.loadingService.isLoading()) {
            this.loadingState = 0;
        } else {
            if (tileCheck) {
                this.loadingState = 2;
            } else {
                if (this.allTiles !== null && this.allTiles.filter((t) => t.layer === this.layerId).length === 0) {
                    this.loadingState = 1;
                }
            }
        }
    }

    public getLayerIndex(): number {
        return this.isFirstLayer ? 1 : this.isSecondLayer ? 2 : 3;
    }

    public getImageUrl(): string {
        let url = '';
        this.translate
            .get(['tiles_nodata_layer1_link', 'tiles_nodata_layer2_link', 'tiles_nodata_layer3_link'])
            .subscribe((translations) => {
                url = translations['tiles_nodata_layer' + this.getLayerIndex() + '_link'];
            });
        return url;
    }

    getNoDataLayerText(): string {
        return this.isFirstLayer ? 'tiles_nodata_text1' : 'tiles_nodata_text1_2';
    }

    getLayerName(): string {
        const layers = this.layerService.getCachedLayers() || [];
        const parentLayerIndex = (layers.find((l) => l.id === this.layerId)?.index ?? 1) - 1;
        return this.isFirstLayer ? this.layerName : (layers[parentLayerIndex - 1]?.name ?? '');
    }

    /**
     * Call this method, when the tiles receive and update
     */
    private onTilesOrLayerUpdate(): void {
        // this.allTiles has changed, so we need to update allTilesOfLayer and filteredTiles as well
        this.allTilesOfLayer = this.allTiles !== null ? this.allTiles.filter((t) => t.layer === this.layerId) : null;

        this.filterTiles();
        this.filterTileParents(); // every time???
    }

    private filterTiles(): void {
        this.areAllTilesSelected = false;
        this.selectedTiles.clear();
        this.confirmPanel?.hide();
        if (this.allTiles === null) return;
        const tt = this.allTiles;
        const temp = tt
            .filter((t) => t.layer === this.layerId)
            .filter((t) => t.archived === this.showArchived)
            .map((t) => (tt ? TileVmMapper.map(t, tt) : null));

        this.hasParent = temp.filter((t) => t && t.parents.length > 0).length > 0;
        this.filteredTiles = JSON.parse(JSON.stringify(temp));
        this.attachUserAccessColumn(this.filteredTiles);
        this.updateTimeBudgetViews();
    }

    private filterTileParents(includeArchived: boolean = false): void {
        if (this.allTiles === null) return;
        const layers = this.layers;
        const parentLayerId = layers.find((l) => l.id === this.filteredTiles[0]?.layer)?.parent;

        if (parentLayerId) {
            // Adjust filtering based on the flag
            const parentTiles = this.allTiles.filter(
                (t) => t.layer === parentLayerId && (includeArchived || !t.archived),
            );
            const parentLayerIndex = (layers.find((l) => l.id === this.layerId)?.index ?? 1) - 1;
            this.tileParents = parentTiles.map((t) => ({
                name: t.name,
                id: t.id,
            }));
            this.parentLayerName = layers[parentLayerIndex - 1]?.name ?? '';
        } else if (!this.isFirstLayer) {
            const parentLayerIndex = (layers.find((l) => l.id === this.layerId)?.index ?? 1) - 1;
            const pLayerId = layers.find((l) => l.index === parentLayerIndex)?.id;
            const parentTiles = this.allTiles.filter((t) => t.layer === pLayerId && (includeArchived || !t.archived));
            this.tileParents = parentTiles.map((t) => ({
                name: t.name,
                id: t.id,
            }));
            this.parentLayerName = layers[parentLayerIndex - 1]?.name ?? '';
        }
    }

    getUserDisplayName(user: User): string {
        // fix: users can have an empty first or last name (e.g. older accounts): show the parts that exist, else the email
        const name = [user.firstName, user.lastName].filter(Boolean).join(' ').trim();
        return name || user.email || `#${user.id}`;
    }

    toggleTileStatus(): void {
        this.showArchived = this.selectedTileStatus;
        this.updatePage(true);
        if (!this.showArchived) {
            this.loadTimeBudgets(); // restored tiles are recounted by the backend
        }
    }

    async openBulkEditPanel(event: Event): Promise<void> {
        this.menuPanel.toggle(event);
        this.loading = true;
        await this.tilesHaveActivitiesCheck([...this.selectedTiles]);
        this.loading = false;
    }

    async tilesHaveActivitiesCheck(tiles: TileVM[]): Promise<void> {
        const tileIds = tiles.map((tile) => tile.id);
        this.tilesHaveActivities = await firstValueFrom(this.tileService.checkTilesHaveActivities(tileIds));
    }

    async onRowSelect(_event: TableRowSelectEvent<TileVM>): Promise<void> {
        if (!this.currentUser?.hasPermission(UserPermission.TILE_EDIT)) {
            return;
        }
        this.filterTileParents(true);
        this.tileToEdit = this.selectedTile ? CloneUtils.cloneData(this.selectedTile) : undefined;
        this.showAddEditDialog = true;
    }

    // UI Helper
    getParentsFromTile(tile: TileVM): string {
        if (tile.type === TileType.Global) {
            return '';
        }

        const parents = tile.parents.map((p) => p.name).join(', ');

        let maxParentsToShow = 10;

        // return the whole string if it fits (length <= 200). Otherwise trim
        if (parents.length <= 200) {
            return parents;
        }

        let trimmedParents = parents.split(', ').slice(0, maxParentsToShow).join(', ');
        let remainingParentsCount = parents.split(', ').length - maxParentsToShow;

        if (trimmedParents.length + ` + ${remainingParentsCount} ${this.getLayerName()}`.length <= 200) {
            return trimmedParents + ` + ${remainingParentsCount} ${this.getLayerName().toUpperCase()}`;
        }

        while (trimmedParents.length + ` + ${remainingParentsCount} ${this.getLayerName()}`.length > 200) {
            maxParentsToShow--;
            trimmedParents = parents.split(', ').slice(0, maxParentsToShow).join(', ');
            remainingParentsCount = parents.split(', ').length - maxParentsToShow;
        }

        return trimmedParents + ` + ${remainingParentsCount} ${this.getLayerName().toUpperCase()}`;
    }

    getBoarder(tile: TileVM): string {
        return tile.color.toLowerCase() === '#ffffff' ? '1px solid #bbb' : tile.color;
    }

    filterByParent(tileParents: TileParent[]): void {
        this.filterTiles();

        if (tileParents.length === 0) {
            return;
        }

        this.filteredTiles = this.filteredTiles.filter((t) =>
            tileParents.map((p) => p.id).some((p) => t.parents.map((tt) => tt.id).includes(p)),
        );
    }

    select(tile: TileVM, checked: boolean): void {
        this.areAllTilesSelected = false;
        tile.isChecked = checked;
        if (checked) {
            this.selectedTiles.add(tile);
        } else {
            this.selectedTiles.delete(tile);
        }
        // dismiss the bulk edit menu dialog
        this.menuPanel.hide();
        this.selTilesFavCheck();
    }

    onPageChange(event: TablePageEvent): void {
        this.firstPagination = event.first;
        this.paginationRows = event.rows;
    }

    selectAll(checked: boolean, event: Event | undefined): void {
        if (checked) {
            this.filteredTiles.slice(this.firstPagination, this.paginationRows + this.firstPagination).forEach((t) => {
                t.isChecked = true;
                this.selectedTiles.add(t);
            });
            const numberOfTilesOnPageIsTheFullList = this.filteredTiles.length !== this.selectedTiles.size;
            if (numberOfTilesOnPageIsTheFullList) {
                this.confirmPanel?.show(event);
            }
        } else {
            this.areAllTilesSelected = checked;
            this.filteredTiles.forEach((t) => {
                t.isChecked = false;
                this.selectedTiles.delete(t);
            });
            this.confirmPanel?.hide();
        }
        this.selTilesFavCheck();
    }

    selTilesFavCheck(): void {
        this.areAllSelTilesFav = Array.from(this.selectedTiles).every((tile: TileVM) => tile.favorite === true);
    }

    async confirmBulkSelection(_panel: Popover): Promise<void> {
        if (this.selectedTiles.size === 0) {
            return;
        }
        this.areAllTilesSelected = true;
        this.filteredTiles.forEach((t) => {
            t.isChecked = true;
            this.selectedTiles.add(t);
        });
        this.confirmPanel?.hide();
    }

    onCancel(): void {
        this.confirmPanel?.hide();
        this.deleteTileDialog.hide();
        // Reset the bulk delete flag when cancelling
        this.isBulkDelete = false;
    }

    showAddDialog(): void {
        this.tileToEdit = TileVmMapper.newTileVm();
        this.filterTileParents(false);
        this.showAddEditDialog = true;
    }

    onBulkSelectionArchiveOrRestore(): void {
        const tileName = this.selectedTile?.name || this.layerName;
        const selTileCount = this.selectedTiles.size;
        const header = this.translate
            .instant(!this.showArchived ? 'confirm_archive_user_header' : 'confirm_unarchive_user_header')
            .replace('{{tileName}}', tileName.toString())
            .replace('{{tileCount}}', selTileCount.toString());
        this.confirmationService.confirm({
            message: !this.showArchived
                ? this.translate
                      .instant('confirm_tile_archive')
                      .replace('{{tileName}}', `${this.selectedTiles.size.toString()} ${this.layerName}`)
                      .replace('{{layerName}}', this.layerName)
                : this.translate.instant('confirm_tile_restore').replace('{{layerName}}', this.layerName),
            key: 'archiveTile',
            header: header,
            icon: 'pi pi-info-circle',
            acceptLabel: !this.showArchived
                ? this.translate.instant('tiles_archive')
                : this.translate.instant('tiles_restore'),
            rejectLabel: this.translate.instant('button_cancel'),
            acceptButtonStyleClass: 'p-button-primary',
            rejectButtonStyleClass: 'p-button-text p-button-primary',
            acceptIcon: 'none',
            rejectIcon: 'none',
            accept: () => {
                this.setLoading(true);
                const updateList: Observable<Tile>[] = [];
                const updateDtos: Tile[] = [];
                this.selectedTiles.forEach((t) => {
                    const tile = this.filteredTiles.find((tt) => tt.id === t.id);
                    if (tile) {
                        tile.archived = !this.showArchived;
                        const dto = TileVmMapper.mapToDto(tile);
                        updateList.push(this.tileService.update(dto));
                        updateDtos.push(dto);
                    }
                });

                this.selectedTiles.clear();

                forkJoin([...updateList]).subscribe(() => {
                    updateDtos.forEach((tile) => this.updateTile(tile));
                    this.setLoading(false);
                    this.selectedTiles.clear();
                });

                TilesComponent.showArchiveOpToast(this.toastService, this.showArchived);
            },
        });
    }

    onFavoriteClicked(): void {
        this.setLoading(true);

        const updateList: Observable<Tile>[] = [];
        const updateDtos: Tile[] = [];
        this.selectedTiles.forEach((t) => {
            const tile = this.filteredTiles.find((tt) => tt.id === t.id);
            if (tile) {
                tile.favorite = this.areAllSelTilesFav ? false : true;
                const dto = TileVmMapper.mapToDto(tile);
                updateList.push(this.tileService.update(dto));
                updateDtos.push(dto);
            }
        });

        this.selectedTiles.clear();

        forkJoin([...updateList]).subscribe(() => {
            updateDtos.forEach((tile) => this.updateTile(tile));
            this.setLoading(false);
            this.selectedTiles.clear();
            this.menuPanel.hide();
        });
    }

    private setLoading(loading: boolean): void {
        this.loadingService.setLoading(loading, this.constructor.name);
    }

    private setLoadingState(state: PageLoadingState): void {
        this.loadingService.setLoadingState(state, this.constructor.name);
    }

    ngOnDestroy(): void {
        this.subscriptions.unsubscribe();
        this.timeBudgetSubscription?.unsubscribe();
    }

    public showTimeBudgets(): boolean {
        return this.canReadTimeBudgets && !this.showArchived;
    }

    // the API returns the saved budget already counted
    public onTimeBudgetChanged(budget: TimeBudget): void {
        this.savedTimeBudget = budget;
        this.setTimeBudgets([...this.timeBudgets.values()]);
    }

    private loadTimeBudgets(): void {
        this.timeBudgetSubscription?.unsubscribe();
        this.savedTimeBudget = undefined; // the new list contains it
        if (!this.canReadTimeBudgets) {
            this.setTimeBudgets([]);
            return;
        }
        this.timeBudgetSubscription = this.timeBudgetService.list().subscribe({
            next: (budgets: TimeBudget[]) => this.setTimeBudgets(budgets),
            error: (error: unknown) => console.error('Loading the time budgets failed:', error),
        });
    }

    private setTimeBudgets(budgets: TimeBudget[]): void {
        const saved = this.savedTimeBudget;
        const current = saved ? [...budgets.filter((budget) => budget.tileId !== saved.tileId), saved] : budgets;
        this.timeBudgets = new Map(current.filter((budget) => budget.active).map((budget) => [budget.tileId, budget]));
        this.updateTimeBudgetViews();
    }

    private updateTimeBudgetViews(): void {
        const views = new Map<number, TimeBudgetView>();
        const layerIndex = this.getLayerIndex();
        for (const tile of this.filteredTiles) {
            const budget = this.timeBudgets.get(tile.id);
            // the API allows budgets on global tiles and tiles with several parents; the page doesn't show them
            if (budget && TimeBudgetUtils.isAllowedFor(tile, layerIndex)) {
                views.set(tile.id, { budget, usedSeconds: TimeBudgetUtils.usedSecondsTotal(budget) });
            }
        }
        this.timeBudgetViews = views;
    }

    attachUserAccessColumn(filteredTiles: TileVM[]): void {
        for (const tile of filteredTiles) {
            if (tile.hasOwnProperty('userAccess')) {
                continue;
            }

            if (tile.isAvailableToAllUsers) {
                tile.assignees = this.translate.instant('tile_public_label');
            } else {
                const userNames = tile.assignedUsers
                    .map((user: User) => user.firstName + ' ' + user.lastName)
                    .join(', ');
                const groupNames = tile.assignedGroups.map((group: Group) => group.name).join(', ');
                tile.assignees = userNames + groupNames;
            }
        }
    }

    /**
     * onTilesModified() provides necessary post processing when tiles are created, edited, deleted or modified in any way.
     */
    private onTilesModified(): void {
        // invalidate the tile cache, so that on the next subscription to tiles$ the tiles are fetched again from the api
        this.tileService.invalidateCache();
    }

    public onTileChanged(tile: Tile): void {
        this.updateTile(tile);
        // the backend recounts the budgets when the parents, type or archived state of a tile change
        this.loadTimeBudgets();
    }

    public onTileAdded(tile: Tile): void {
        this.allTiles = this.allTiles ? [...this.allTiles, tile] : [tile];
        this.onTilesOrLayerUpdate();
        this.loadingState = 2;
    }

    public onTileDeleted(tile: Tile): void {
        if (this.allTiles === null) return;
        this.allTiles = this.allTiles.filter((t) => t.id !== tile.id);
    }

    public onTileDialogClose(): void {
        this.showAddEditDialog = false;
        // reset row selection - only works correctly when setting it to null (not undefined)
        this.selectedTile = null;
    }

    public static showConfirmDeleteDialog(
        confirmationService: ConfirmationService,
        translate: TranslateService,
        tileName: string,
    ): void {
        confirmationService.confirm({
            message: translate.instant('delete_tile_message').replace('{{tileName}}', tileName),
            key: 'deleteTile',
            header: translate.instant('tile_deletion_confirm_header').replace('{{tileName}}', tileName),
            icon: 'none',
            acceptButtonStyleClass: 'p-button-danger',
            rejectButtonStyleClass: 'p-button-text p-button-primary',
            acceptLabel: translate.instant('button_delete'),
            rejectLabel: translate.instant('button_cancel'),
            acceptIcon: 'none',
            rejectIcon: 'none',
        });
    }

    public static showArchiveOpToast(toastService: ToastService, showArchived: boolean): void {
        if (!showArchived) {
            toastService.success('tiles_archived', 'tiles_archive_success');
        } else {
            toastService.success('tiles_restored', 'tiles_restore_success');
        }
    }
}
